import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useT } from '../../hooks/useT';
import { getIcon } from '../../utils/iconRegistry';
import { acquireModalLock } from '../../utils/modalLock';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import styles from './Sidebar.module.css';

const NAV_ID = 'sidebar-nav';

// Only emit a custom property when the config actually carries a value, so the
// fallback declared in Sidebar.module.css (the theme variable) stays in effect.
function cssVars(vars) {
  return Object.entries(vars).reduce((acc, [name, value]) => {
    if (value) acc[name] = value;
    return acc;
  }, {});
}

function Sidebar() {
  const config = useSiteConfig();
  const t = useT();
  const sidebar = config.sidebar;
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(!isMobile);
  const location = useLocation();
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);
  const focusNavRef = useRef(false);
  const restoreFocusRef = useRef(false);

  // On desktop the column is always laid out; only the mobile drawer opens/closes.
  const isExpanded = !isMobile || isOpen;
  const sidebarWidth = sidebar.width;

  const openNav = useCallback(() => {
    focusNavRef.current = true;
    setIsOpen(true);
  }, []);

  const closeNav = useCallback((restoreFocus = true) => {
    restoreFocusRef.current = restoreFocus;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobile) setIsOpen(true);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    // Navigating away hides the drawer; pull focus back out of it if it lived there.
    restoreFocusRef.current = !!(navRef.current && navRef.current.contains(document.activeElement));
    setIsOpen(false);
  }, [location, isMobile]);

  useEffect(() => {
    if (isOpen) {
      if (focusNavRef.current) {
        focusNavRef.current = false;
        if (navRef.current) navRef.current.focus();
      }
      return;
    }
    focusNavRef.current = false;
    if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      if (hamburgerRef.current) hamburgerRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isMobile || !isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeNav();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, isOpen, closeNav]);

  useEffect(() => {
    // The mobile drawer covers the page behind a backdrop, so it owns the keyboard
    // while it is open (App's global shortcuts bail out on this attribute).
    if (!isMobile || !isOpen) return undefined;
    return acquireModalLock();
  }, [isMobile, isOpen]);

  useEffect(() => {
    // `--sidebar-width` is declared on :root (variables.css / useThemeApplier).
    // Overriding it on <body> lets sidebar.width drive both this column and the
    // main content offset that reads the same variable.
    if (!sidebarWidth) return undefined;
    document.body.style.setProperty('--sidebar-width', sidebarWidth);
    return () => document.body.style.removeProperty('--sidebar-width');
  }, [sidebarWidth]);

  if (!sidebar.enabled) return null;

  const navItems = (sidebar.navItems || []).filter(item => item.enabled !== false);

  const sidebarStyle = cssVars({
    '--fgl-sidebar-bg': sidebar.backgroundColor,
    '--fgl-sidebar-text': sidebar.textColor,
    '--fgl-sidebar-active': sidebar.activeColor,
    '--fgl-sidebar-hover': sidebar.hoverColor,
    '--fgl-sidebar-border': sidebar.borderColor,
    '--fgl-sidebar-brand-size': sidebar.brandFontSize,
  });

  return (
    <>
      {isMobile && (
        <button
          ref={hamburgerRef}
          type="button"
          className={styles.hamburger}
          onClick={() => (isOpen ? closeNav(false) : openNav())}
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
          aria-controls={NAV_ID}
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      )}

      {isMobile && isOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={() => closeNav()}
          aria-label="Close navigation"
        />
      )}

      <nav
        id={NAV_ID}
        ref={navRef}
        tabIndex={-1}
        aria-label="Main navigation"
        className={`${styles.sidebar} ${isExpanded ? styles.open : styles.closed}`}
        style={sidebarStyle}
      >
        {sidebar.showBrand && (
          <div className={styles.brand}>
            <span className={styles.brandText}>{sidebar.brandText}</span>
          </div>
        )}

        <ul className={styles.navList}>
          {navItems.map((item, index) => {
            const Icon = getIcon(item.icon);
            const itemStyle = cssVars({
              '--fgl-nav-font-size': item.fontSize,
              '--fgl-nav-font-weight': item.fontWeight,
              '--fgl-nav-padding-y': item.paddingY,
              '--fgl-nav-padding-x': item.paddingX,
              '--fgl-nav-margin-bottom': item.marginBottom,
            });
            return (
              <li key={item.id || `nav-${index}`} className={styles.navItem} style={itemStyle}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.active : ''}`
                  }
                  end={item.path === '/'}
                >
                  {Icon && <Icon className={styles.navIcon} />}
                  <span className={styles.navLabel}>{t(item.labelKey)}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>

        {sidebar.showLanguageSwitcher && <LanguageSwitcher />}
        {sidebar.showFooter && (
          <div className={styles.sidebarFooter}>
            <span className={styles.footerText}>{sidebar.footerText}</span>
          </div>
        )}
      </nav>
    </>
  );
}

export default Sidebar;
